import React, { FC, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { DateTime } from 'luxon'

import { selectors } from '../../redux'
import { isSameDay } from '../../helpers/DateHelpers'

import * as SC from './styled'

type Cell = {
  date: Date
  type: 'available' | 'unavailable'
}

export type CalendarProps = {
  className?: string
  title?: string
  legend?: string
  selectedDate?: Date
  cells: Cell[]

  onSelect?: (date: Date) => void
}

type ValuePiece = Date | null

type Value = ValuePiece | [ValuePiece, ValuePiece]

const Calendar: FC<CalendarProps> = ({
  className,
  title,
  legend,
  cells,
  onSelect,
  selectedDate,
}) => {
  const currentDay = useRef(new Date())

  const [value, onChange] = useState<Value>(new Date())
  const locale = useSelector(selectors.app.currentLocale)

  return (
    <SC.Container className={className}>
      <SC.Title>{title}</SC.Title>
      <SC.Legend>{legend}</SC.Legend>
      <SC.Calendar
        tileClassName={({ date }) => {
          const cell = cells.find((cell) => isSameDay(cell.date, date))
          const isSelectedDate = selectedDate ? isSameDay(cell?.date, selectedDate) : false

          let className = `date`
          className += isSelectedDate ? ' selected' : ''

          switch (cell?.type) {
            case 'available':
              className += ' available'
              break
            case 'unavailable':
              className += ' unavailable'
              break
          }

          return className.trim()
        }}
        value={value}
        onChange={onChange}
        tileContent={({ date }) => {
          const isCurrentDay = isSameDay(currentDay.current, date)

          return isCurrentDay ? <SC.CurrentDayCircle /> : <></>
        }}
        maxDetail="month"
        minDetail="month"
        prev2Label={null}
        next2Label={null}
        onClickDay={(date) => onSelect?.(date)}
        formatDay={(_, date) =>
          DateTime.fromJSDate(date, { zone: 'Europe/Paris' }).setLocale(locale).toFormat('dd')
        }
        formatShortWeekday={(_, date) =>
          DateTime.fromJSDate(date, { zone: 'Europe/Paris' }).setLocale(locale).toFormat('ccc')
        }
        navigationLabel={({ date }) => {
          return (
            <>
              <SC.CalendarMonth>
                {DateTime.fromJSDate(date).setLocale(locale).toFormat('LLLL yyyy')}
              </SC.CalendarMonth>
              <SC.CalendarIcon />
            </>
          )
        }}
      />
    </SC.Container>
  )
}

export default Calendar
